/**
 * Private function to check if a value is null, undefined, or an empty string
 */
function _isValueEmpty(val) {
    return val === '' || val == null;
}
/**
 * Returns an anchor element which is used as a url parser and editor.
 * Anchor elements implement the HTMLHyperlinkElementUtils interface.
 */
export const parseURL = function (url) {
    const a = document.createElement('a');
    a.href = url;
    try {
        return new URL(a.href);
    }
    catch (ex) {
        return a;
    }
};
/**
 * Check if the given url is a local url (i.e has same origin)
 *
 * If the strict flag is given, this will try to match the document.baseURI
 * instead of just the origin
 *
 * E.g: given a document with baseURI 'https://staizen.com/base'
 * - isLocalURL('/test')           will return true since its has the same origin
 * - isLocalURL('/test',true)      will return false since the baseURI is different
 * - isLocalURL('/base/test',true) will return true since its has the same baseURI
 */
export const isLocalURL = function (url, strict) {
    const urlParser = parseURL(url);
    let base = window.location.origin;
    if (strict && document.head.querySelector('base')) {
        base = document.baseURI;
    }
    return urlParser.href.indexOf(base) === 0;
};
/**
 * Return the window's url's origin portion.
 * Same as window.location.origin on browsers that support
 */
export const getWindowOrigin = function () {
    return window.location.origin || parseURL(window.location.href).origin;
};
/**
 * Returns the given url's base path / slug
 */
export const getBasePath = function (url) {
    return url.replace(getWindowOrigin(), '').replace(/\/$/, '');
};
/**
 * Return wether the url is an external url or not.
 * It compares the document's domain and the domain of the url given in parameter.
 *
 */
export const isExternalURL = function (url, strict) {
    return isLocalURL(url, strict) === false;
};
/**
 * Returns the document's base path
 */
export const getWindowBasePath = function () {
    return getBasePath(document.baseURI);
};
/**
 * Encodes the given parameter object into a query string that can be used
 * to replace the search portion of a url. The reverse of decodeQueryString
 */
export const encodeQueryString = function (params, ignoreEmpty) {
    return `?${Object.keys(params)
        .reduce((arr, key) => {
        const val = params[key];
        if (ignoreEmpty && _isValueEmpty(val)) {
            return arr;
        }
        arr.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
        return arr;
    }, [])
        .join('&')}`;
};
/**
 * Parses a query string into an object. The reverse of encodeQueryStying.
 */
export const decodeQueryString = function (qs, ignoreEmpty) {
    return qs
        .replace('?', '')
        .split('&')
        .reduce((result, it) => {
        if (it) {
            const split = it.split('=');
            if (ignoreEmpty && _isValueEmpty(split[1])) {
                return result;
            }
            result[decodeURIComponent(split[0])] = decodeURIComponent(split[1]);
        }
        return result;
    }, {});
};
/**
 * Parses the url and return it's query params as an object.
 */
export const getQueryParams = function (url, ignoreEmpty) {
    return decodeQueryString(parseURL(url).search, ignoreEmpty);
};
/**
 * Parses the given url and returns a new url with the
 * given params encoded as it's query string
 */
export const setQueryParams = function (url, params, ignoreEmpty) {
    const urlParser = parseURL(url);
    urlParser.search = encodeQueryString(params, ignoreEmpty);
    return urlParser.href;
};
/**
 * Remove params from an object that have empty values
 */
export const removeEmptyParams = function (params) {
    Object.keys(params).map(k => {
        if (Object.prototype.hasOwnProperty.call(params, k) && _isValueEmpty(params[k])) {
            delete params[k];
        }
        return null;
    });
};
